import styled from 'styled-components';
import { Box } from 'components/primitives/box';
import { Text } from 'components/primitives/typography';
import { FlexAlignCenterSpaceBetween, FlexCenterBoth, ItemFlex1 } from 'components/primitives/flex';
import { useAtom, useAtomValue } from 'jotai';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import { mediaMMdMin } from 'helpers/breakpoints';
import { useShowAdminView } from './hooks/useAdminView';
import { useSelectDefaultTab } from './hooks/useSelectDefaultTab';
import useConversationsQueries from './hooks/useConversationsQueries';
import useSelectFirstConversationInTab from './hooks/useSelectFirstConversation';
import { messagesFilterOptions, TabNames } from './constants';
import { activeTabNameAtom, messagesFilterAtom } from './store';
import ConversationsTabContent from './ConversationsTabContent';
import { Loader, panelScrollMixin } from './commons';
import Tabs, { Tab } from './Tabs';
import CustomSelect from './CustomSelect';
import useInboxUnreadCount from './hooks/useInboxUnreadCount';

export default function ConversationsPanel() {
  const [filterValue, setFilterValue] = useAtom(messagesFilterAtom);
  const { isAnyQueryLoading, renter, owner, captain } = useConversationsQueries();
  const selectFirstConversation = useSelectFirstConversationInTab();
  const activeTabName = useAtomValue(activeTabNameAtom);
  const showAdminView = useShowAdminView();
  const { inboxCount } = useInboxUnreadCount();
  const rudderTrack = useRudderStackTrack();

  useSelectDefaultTab();

  const renterTab = (
    <Tab
      label="As Renter"
      name={TabNames.renter}
      labelProps={{ unreadCount: inboxCount?.messages?.renter, isRenter: true }}
    >
      <ConversationsTabContent queryObject={renter.query} data-testid="inbox-renter-tab-panel-content" />
    </Tab>
  );

  return (
    <Wrapper id="inboxMessagesPanel">
      <Box py="32px">
        <FlexAlignCenterSpaceBetween gap="80px" mb="32px" px="24px">
          <Text fSize="24px" fWeight="700">
            Messages
          </Text>
          <ItemFlex1 maxWidth="140px">
            <CustomSelect
              value={filterValue}
              options={Object.values(messagesFilterOptions)}
              userVariant={activeTabName}
              onChange={(selectedOption) => {
                rudderTrack('inbox_filter_select', {
                  value: selectedOption,
                });
                setFilterValue(selectedOption);
              }}
              isSearchable={false}
            />
          </ItemFlex1>
        </FlexAlignCenterSpaceBetween>

        <Tabs
          CustomLabelComponent={CustomTabLabel}
          activeTabNameAtom={activeTabNameAtom}
          onTabChange={selectFirstConversation}
          disabled={showAdminView}
        >
          {renter.isTabEnabled && renterTab}
          {owner.isTabEnabled && (
            <Tab
              label="As Owner"
              name={TabNames.owner}
              labelProps={{ unreadCount: inboxCount?.messages?.owner, isOwner: true }}
            >
              <ConversationsTabContent queryObject={owner.query} />
            </Tab>
          )}
          {captain.isTabEnabled && (
            <Tab
              label="As Captain"
              name={TabNames.captain}
              labelProps={{ unreadCount: inboxCount?.messages?.captain, isCaptain: true }}
            >
              <ConversationsTabContent queryObject={captain.query} />
            </Tab>
          )}
          {/* always render at least renter tab at the end */}
          {!renter.isTabEnabled && renterTab}
        </Tabs>
      </Box>
      {isAnyQueryLoading && (
        <LoaderWrapper data-testid="loader">
          <Loader />
        </LoaderWrapper>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  ${panelScrollMixin}
  position: relative;
`;

const CustomTabLabel = styled.div<{
  isRenter: boolean;
  isOwner: boolean;
  isCaptain: boolean;
  active: boolean;
  unreadCount: number;
}>`
  border-top: solid 2px transparent;
  font-weight: 700;
  color: #dbdfe5;
  padding: 16px 0;
  margin-bottom: 16px;

  &::after {
    content: ${({ unreadCount }) => unreadCount > 0 && `"${unreadCount}"`};
    width: auto;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    display: inline-block;
    background: #f4c064;
    color: #17233c;
    border-radius: 50px;
    position: relative;
    top: -6px;
    left: 6px;
    font-weight: 700;
    font-size: 10px;
    line-height: 16px;
    text-align: center;
  }

  ${({ isRenter, active }) =>
    isRenter &&
    active &&
    `
      border-top-color: #0a4195;
      color: #0a4195;
    `};

  ${({ isOwner, active }) =>
    isOwner &&
    active &&
    `
      border-top-color: #0e7873;
      color: #0e7873;
    `};

  ${({ isCaptain, active }) =>
    isCaptain &&
    active &&
    `
      border-top-color: #154353;
      color: #154353;
    `};
`;

const LoaderWrapper = styled(FlexCenterBoth)`
  height: 100%;
  max-height: calc(100vh - 60px);
  backdrop-filter: brightness(80%);
  z-index: 200;
  position: fixed;
  inset: 0;
  top: 60px;

  ${mediaMMdMin} {
    z-index: 20;
    position: sticky;
    inset: unset;
    bottom: 0;
  }
`;
